/**
 * Helpers to extract tooltip errors from provider responses.
 * Use only when building tooltip text; do not modify provider services.
 */
import type { VerificationStatusForTooltip } from './tooltip-builder';
import { TooltipError } from './tooltip-error.enum';

/** USPS address validation response shape (minimal; use only for tooltip extraction) */
export function getUSPSTooltipErrors(
  r: { hasCorrections?: boolean; corrections?: string[] } | null | undefined
): TooltipError[] {
  if (!r?.hasCorrections) return [];

  const c = (r.corrections ?? []).join(' ').toLowerCase();
  const errors: TooltipError[] = [];

  if (c.includes('zip')) errors.push(TooltipError.INVALID_ZIP);
  if (c.includes('state')) errors.push(TooltipError.INVALID_STATE);
  if (c.includes('city')) errors.push(TooltipError.INVALID_CITY);
  if (c.includes('street')) errors.push(TooltipError.INVALID_STREET);

  if (!errors.length) errors.push(TooltipError.GENERIC_ADDRESS);

  return errors;
}

/** Lyons ACH result_details shape (minimal; use only for tooltip extraction). API may send camelCase or snake_case. */
type LyonsACHResultDetails = Record<string, unknown>;

/** Convert camelCase to snake_case for key lookup */
function toSnakeCase(camel: string): string {
  return camel.replace(/([A-Z])/g, '_$1').toLowerCase();
}

/** Read boolean from result_details with camelCase or snake_case key (accepts boolean or string "true"/"false") */
function getBool(r: LyonsACHResultDetails | null | undefined, camel: string): boolean | undefined {
  if (!r) return undefined;
  const val = r[camel] ?? r[toSnakeCase(camel)];
  if (typeof val === 'boolean') return val;
  if (val === 'true') return true;
  if (val === 'false') return false;
  return undefined;
}

/** Read number from result_details with camelCase or snake_case key (accepts number or numeric string) */
function getNum(r: LyonsACHResultDetails | null | undefined, camel: string): number | undefined {
  if (!r) return undefined;
  const val = r[camel] ?? r[toSnakeCase(camel)];
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val === 'string') {
    const n = Number(val);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/** Lyons ACH: all errors in one array (for single-row use) */
export function getLyonsACHTooltipErrors(
  r: LyonsACHResultDetails | null | undefined
): TooltipError[] {
  const { ownershipErrors, accountStatusErrors } = getLyonsACHTooltipErrorsByRow(r);
  return [...ownershipErrors, ...accountStatusErrors];
}

/**
 * Lyons ACH: errors split by row. Use for "Account Ownership" vs "Account Status" rows.
 * - ownershipErrors: ownership-only (e.g. OWNERSHIP_NOT_VERIFIED) → Account Ownership row
 * - accountStatusErrors: routing + account status (each on its own line) → Account Status row
 * Reads both camelCase and snake_case keys so we show all failures when API sends either format.
 */
export function getLyonsACHTooltipErrorsByRow(r: LyonsACHResultDetails | null | undefined): {
  ownershipErrors: TooltipError[];
  accountStatusErrors: TooltipError[];
} {
  const ownershipErrors: TooltipError[] = [];
  const accountStatusErrors: TooltipError[] = [];
  if (!r) return { ownershipErrors, accountStatusErrors };

  const routingValid = getBool(r, 'routingNumberValid');
  const accountStatusCode = getNum(r, 'accountStatusCode');
  const ownershipStatusCode = getNum(r, 'ownershipStatusCode');

  // 101 = ACCEPT (ownership verified); any other code means ownership not verified
  if (ownershipStatusCode != null && ownershipStatusCode !== 101) {
    ownershipErrors.push(TooltipError.OWNERSHIP_NOT_VERIFIED);
  }

  // Account status row: add every applicable error so tooltip can show multiple lines
  if (routingValid === false) accountStatusErrors.push(TooltipError.INVALID_ROUTING);
  if (accountStatusCode === 103) accountStatusErrors.push(TooltipError.ACCOUNT_NOT_VERIFIED);
  if (accountStatusCode === 104) accountStatusErrors.push(TooltipError.INVALID_ACCOUNT);
  // If we have a non-101 account code that we didn't map, still show a generic account failure
  if (
    accountStatusCode != null &&
    accountStatusCode !== 101 &&
    accountStatusCode !== 103 &&
    accountStatusCode !== 104
  ) {
    accountStatusErrors.push(TooltipError.ACCOUNT_NOT_VERIFIED);
  }

  return { ownershipErrors, accountStatusErrors };
}

/**
 * Ownership is "inconclusive" when the routing number and account are valid
 * but the ownership check itself returned a non-101 code.
 * This means the account exists — ownership just couldn't be confirmed.
 */
export function isOwnershipInconclusive(r: LyonsACHResultDetails | null | undefined): boolean {
  if (!r) return false;
  const routingValid = getBool(r, 'routingNumberValid');
  const accountStatusCode = getNum(r, 'accountStatusCode');
  const ownershipStatusCode = getNum(r, 'ownershipStatusCode');
  return (
    routingValid === true &&
    accountStatusCode === 101 &&
    ownershipStatusCode != null &&
    ownershipStatusCode !== 101
  );
}

/**
 * Account status is "inconclusive" when the routing number is valid
 * but the account status code is 103 (unable to verify — not outright invalid).
 */
export function isAccountStatusInconclusive(r: LyonsACHResultDetails | null | undefined): boolean {
  if (!r) return false;
  const routingValid = getBool(r, 'routingNumberValid');
  const accountStatusCode = getNum(r, 'accountStatusCode');
  return routingValid === true && accountStatusCode === 103;
}

/**
 * Status for the "Account Status" row only. Lyons returns one combined ACH payload; the overall
 * `status` can be UNVERIFIED when ownership fails even if routing + account status are fine.
 * This row should reflect account/routing health from `result_details`, not ownership.
 *
 * @see MR !407 discussion — ownership inconclusive must not paint Account Status red when accountStatusCode is 101.
 */
export function resolveLyonsAchAccountStatusRowStatus(
  r: LyonsACHResultDetails | null | undefined,
  fallback: VerificationStatusForTooltip
): VerificationStatusForTooltip {
  if (!r) return fallback;
  const routingValid = getBool(r, 'routingNumberValid');
  const accountStatusCode = getNum(r, 'accountStatusCode');
  if (routingValid === true && accountStatusCode === 101) return 'VERIFIED';
  if (routingValid === true && accountStatusCode === 103) return 'UNVERIFIED';
  if (routingValid === false) return 'UNVERIFIED';
  if (accountStatusCode === 104) return 'UNVERIFIED';
  if (
    accountStatusCode != null &&
    accountStatusCode !== 101 &&
    accountStatusCode !== 103 &&
    accountStatusCode !== 104
  ) {
    return 'UNVERIFIED';
  }
  return fallback;
}

/** Lyons OFAC response shape (minimal; use only for tooltip extraction) */
export function getLyonsOFACTooltipErrors(
  r: { hasTruePositives?: boolean; hasMatches?: boolean } | null | undefined
): TooltipError[] {
  if (!r) return [];
  if (r.hasTruePositives) return [TooltipError.SANCTION_MATCH];
  if (r.hasMatches) return [TooltipError.POSSIBLE_SANCTION_MATCH];
  return [];
}
